// PendingQuestionDetail.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  message,
  Card,
  Tabs,
  Tag,
  Input,
  Modal,
  Alert,
  Space,
} from 'antd';
import axios from 'axios';
import { AuthContext } from '../components/AuthContext';
import { ReloadOutlined } from '@ant-design/icons';
import TypewriterResponsePlain from '../components/TypeWriterResponsePlain';

const url = new URL(window.location.href);
const finalUrl = `/api/`;
const { TextArea } = Input;

const PendingQuestionDetail = () => {
  const { id } = useParams();
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Add a state to control the modal box in the component state
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    const fetchPendingQuestion = async () => {
      try {
        const res = await axios.get(`${finalUrl}pending-questions/${id}`);
        setPendingQuestion(res.data.data);
      } catch (error) {
        message.error('Failed to fetch approval details');
        navigate('/pending-questions');
      }
    };
    fetchPendingQuestion();
  }, [id, navigate]);

  // Render the modal box
  const renderRejectModal = () => (
    <Modal
      title="Enter the reason for rejection"
      visible={rejectModalVisible}
      onOk={handleConfirmReject}
      onCancel={() => setRejectModalVisible(false)}
      confirmLoading={isRejecting}
      okText="Confirm rejection"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
    >
      <Input.TextArea
        rows={4}
        placeholder="Please provide a detailed reason for rejection (at least 10 characters)"
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        showCount
        maxLength={500}
      />
    </Modal>
  );

  const handleConfirmReject = async () => {
    if (rejectReason.trim().length < 10) {
      message.warning(
        'The reason for rejection must be at least 10 characters long.',
      );
      return;
    }

    setIsRejecting(true);
    try {
      // Call the backend API
      await axios.post(`${finalUrl}pending-questions/${id}/reject`, {
        reviewerId: user.id,
        reason: rejectReason,
      });

      message.success('The question has been rejected.');

      const res = await axios.get(finalUrl.concat('pending-questions'), {
        params: {
          role: user.role === 'manager' ? 'reviewer' : 'submitter',
          userId: user.id,
          status: 'pending',
        },
      });

      const pendingList = res.data;
      const currentIndex = pendingList.findIndex(
        (item) => item.id === parseInt(id),
      );

      if (pendingList.length === 0) {
        navigate('/pending-questions');
      } else if (currentIndex === -1) {
        navigate(`/pending-questions/${pendingList[0].id}`);
      } else if (currentIndex < pendingList.length - 1) {
        navigate(`/pending-questions/${pendingList[currentIndex + 1].id}`);
      } else {
        navigate('/pending-questions');
      }

      // Refresh the data
      // const { data } = await axios.get(`${finalUrl}pending-questions/${id}`);
      // setPendingQuestion(data);

      // Reset the state
      setRejectModalVisible(false);
      setRejectReason('');
    } catch (error) {
      message.error(error.response?.data?.error || 'Operation failed.');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${finalUrl}pending-questions/${id}/approve`);
      message.success('Approved! Moved to official question bank');
      // navigate('/pending-questions');

      const res = await axios.get(finalUrl.concat('pending-questions'), {
        params: {
          role: user.role === 'manager' ? 'reviewer' : 'submitter',
          userId: user.id,
          status: 'pending',
        },
      });

      const pendingList = res.data.filter((item) => item.status === 'pending');
      const currentIndex = pendingList.findIndex(
        (item) => item.id === parseInt(id),
      );

      if (pendingList.length === 0) {
        navigate('/pending-questions');
      } else if (
        currentIndex === -1 ||
        currentIndex === pendingList.length - 1
      ) {
        navigate(`/pending-questions/${pendingList[0].id}`);
      } else {
        navigate(`/pending-questions/${pendingList[currentIndex + 1].id}`);
      }
    } catch (error) {
      message.error('Approval failed: ' + error.response?.data?.message);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      return message.warning('Please enter the reason for rejection');
    }

    setLoading(true);
    try {
      await axios.post(`${finalUrl}pending-questions/${id}/reject`, {
        reason: rejectReason,
      });
      message.success('Question rejected');
      // Reload the latest status
      // loadQuestion();
      setRejectReason('');
      navigate('/pending-questions');
    } catch (error) {
      message.error('Operation failed: ' + error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = async () => {
    try {
      navigate(`/questions/edit`, {
        // Use edit route without ID
        state: {
          originalData: pendingQuestion, // Pass the complete original question data
          isResubmit: true, // Mark as resubmission mode
        },
      });
    } catch (error) {
      message.error(
        'Failed to create a new version: ' +
          (error.response?.data?.error || 'Service unavailable'),
      );
    }
  };

  if (!pendingQuestion)
    return <div className="loading-spinner">Loading...</div>;

  return (
    <Card
      title={`Pending Question - ${pendingQuestion.question_title}`}
      extra={
        <Space size="middle">
          {(user.role === 'reviewer' || user.role === 'manager') && (
            <Button
              key="approve"
              type="primary"
              onClick={handleApprove}
              disabled={
                pendingQuestion.status !== 'pending' ||
                user.id === pendingQuestion.submitter_id
              }
            >
              Approve
            </Button>
          )}
          {(user.role === 'reviewer' || user.role === 'manager') && (
            <Button
              key="reject"
              danger
              onClick={() => setRejectModalVisible(true)}
              disabled={
                pendingQuestion.status !== 'pending' ||
                user.id === pendingQuestion.submitter_id
              }
            >
              Reject
            </Button>
          )}
        </Space>
      }
    >
      <div className="status-tag">
        Current Status:
        {/* <Tag
          color={
            pendingQuestion.status === 'pending'
              ? 'orange'
              : pendingQuestion.status === 'approved'
                ? 'green'
                : 'red'
          }
        >
          {pendingQuestion.status.toUpperCase()}
        </Tag> */}
        <Tag
          color={
            pendingQuestion.status === 'pending'
              ? 'orange'
              : pendingQuestion.status === 'approved'
                ? 'green'
                : 'red'
          }
        >
          {pendingQuestion.status?.toUpperCase() ?? ''}
        </Tag>
      </div>

      <div className="mt-2 text-gray-600 text-sm">
        {pendingQuestion.status === 'rejected' && (
          <Alert
            type="error"
            message={
              <>
                <strong>Reason for rejection:</strong>
                {pendingQuestion.rejection_reason || 'No detailed explanation'}
                <div className="mt-2 text-gray-600 text-sm">
                  <span>
                    Reviewer: {pendingQuestion.reviewer_name || 'Unknown'}
                  </span>
                  <span style={{ marginLeft: 20 }}>
                    Time:
                    {/* {new Date(pendingQuestion.updated_at).toLocaleString()} */}
                    {pendingQuestion.rejection_time
                      ? new Date(
                          pendingQuestion.rejection_time,
                        ).toLocaleString()
                      : 'No record'}
                  </span>
                </div>
              </>
            }
            showIcon
            style={{ margin: '16px 0' }}
          />
        )}
      </div>

      <Tabs defaultActiveKey="detail">
        <Tabs.TabPane tab="Question Metadata" key="metadata">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Submitted By">
              {pendingQuestion.submitter_full_name ||
                `User #${pendingQuestion.submitter_full_name}`}
            </Descriptions.Item>
            <Descriptions.Item label="Submission Time">
              {new Date(pendingQuestion.submit_time).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Reviewer">
              {pendingQuestion.reviewer_full_name}{' '}
            </Descriptions.Item>
            <Descriptions.Item label="Exam Board">
              {pendingQuestion.exam_board || 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label="Subject">
              {pendingQuestion.subject}
            </Descriptions.Item>
          </Descriptions>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Question Description" key="content">
          <div className="content-section">
            {pendingQuestion.question_image && (
              <div className="diagram-preview">
                <h4>Diagrams and Figures</h4>
                <img
                  src={pendingQuestion.question_image}
                  alt="Question Diagram"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            )}
            <h3>Problem Conditions</h3>

            <TypewriterResponsePlain
              content={pendingQuestion.problem_conditions}
            />
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Specific Questions" key="questions">
          {(pendingQuestion.specific_questions || []).map((question, index) => (
            <div key={index} className="solution-block">
              <Card title={`Question ${question.letter?.toUpperCase() ?? ''}`}>
                <TypewriterResponsePlain content={question.latex} />
              </Card>
            </div>
          ))}
        </Tabs.TabPane>

        <Tabs.TabPane tab="Solution Content" key="solutions">
          {(pendingQuestion.solution_content || []).map((solution, index) => (
            <div key={index} className="solution-block">
              <Card title={`Part ${solution.part?.toUpperCase() ?? ''}`}>
                <TypewriterResponsePlain content={solution.latex} />
              </Card>
            </div>
          ))}
        </Tabs.TabPane>
      </Tabs>
      {pendingQuestion.status === 'rejected' &&
        user.id === pendingQuestion.submitter_id && (
          <div className="mt-6 pt-4 border-t border-dashed border-gray-200 text-center">
            <Button
              type="primary"
              onClick={handleResubmit}
              icon={<ReloadOutlined />}
            >
              Edit and Resubmit
            </Button>
            <div className="text-gray-500 mt-2 text-sm">
              Note: Resubmitting will create a new version, and the original
              record will be retained.
            </div>
          </div>
        )}
      {renderRejectModal()}
    </Card>
  );
};

export default PendingQuestionDetail;
